import React, { ElementType, ReactElement, ReactNode, useState } from "react";

import styled, { css } from "styled-components";

import { LinkBranchOrPullRequestDialog } from "./LinkBranchOrPullRequestDialog";
import { LinkingCommandsButton } from "./LinkingCommandsButton";
import { RoundAvatar } from "./RoundAvatar";
import { AVATAR_COLORS } from "./tokens";
import { AvatarGroup } from "../../components/AvatarGroup";
import { Button } from "../../components/Button";
import { AddToListButton } from "../../components/Button/AddToListButton";
import { Chip } from "../../components/Chip";
import { Dialog } from "../../components/Dialog";
import { IconButton } from "../../components/IconButton";
import {
  ArrowRightIcon,
  CancelCrossIcon,
  CheckboxBlankTogglerIcon,
  CheckIcon,
  CircleDotIcon,
  CloseIcon,
  CloudDeployFailedIcon,
  CloudDeployIcon,
  EyeIcon,
  GearFailedIcon,
  GearIcon,
  GitBranchIcon,
  GitCommitIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  GitRepositoryIcon,
  InfoSmallIcon,
  MessageIcon,
  MessageSmallIcon,
  TrashIcon,
} from "../../components/Icons";
import { IStepSequenceStep, StepSequence } from "../../components/StepSequence";
import { Tooltip } from "../../components/Tooltip";
import { Body1, Caption1, Header3 } from "../../components/Typography";
import { colors } from "../../utils";

/**
 * Source Code section of a task — the shared subsystem behind the
 * `Presentation/Source/Task Source` stories (Mix / All Open / All Completed).
 * Holds the row types (issue / pull-or-merge request / branch), the pipeline
 * spine, the history dialog, and the section that lays them out. Extracted into
 * `shared/` so all three stories render the same components; editing a row type
 * here updates every story at once.
 */

const SANS =
  '-apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue", Arial, sans-serif';

/* Icons in the collection colour either via `stroke` (outline glyphs whose root
   is `fill="none"`) or via `fill`. To tint a history glyph by tone we must set
   the right one — this set marks the stroke-based glyphs used in the stream. */
const STROKE_ICONS = new Set<ElementType>([
  GitBranchIcon,
  GitCommitIcon,
  GitPullRequestIcon,
  GitPullRequestDraftIcon,
  GitPullRequestClosedIcon,
  GitMergeIcon,
  GearIcon,
  GearFailedIcon,
  CloudDeployIcon,
  CloudDeployFailedIcon,
  GitRepositoryIcon,
]);

/** Every event glyph renders in the standard muted gray; success / failure is
    carried by a trailing check / cross after the title instead of the glyph. */
const iconColorProps = (
  icon: ElementType
): { stroke: string } | { fill: string } =>
  STROKE_ICONS.has(icon)
    ? { stroke: "var(--color-theme-600)" }
    : { fill: "var(--color-theme-600)" };

/* ================================================================== */
/* Issue state glyphs (open / closed) — mirror the app's IssueStateIcon */
/* (`CircleDotIcon` when open, filled `CheckboxBlankTogglerIcon` when    */
/* closed). Kept as a helper so the row lead and the status chip share   */
/* the same glyph.                                                       */
/* ================================================================== */

const IssueStateIcon = ({
  closed,
  size = 16,
}: {
  closed: boolean;
  size?: number;
}): ReactElement =>
  closed ? (
    <CheckboxBlankTogglerIcon
      width={size}
      height={size}
      fill="var(--color-primary)"
    />
  ) : (
    <CircleDotIcon width={size} height={size} />
  );

/* ================================================================== */
/* Source Code section — data model                                    */
/* ================================================================== */

type StageState = "done" | "progress" | "failed" | "todo";

const STAGE_NAMES = [
  "Branch",
  "Commits",
  "Pull request",
  "CI / Build",
  "Deploy",
];

interface Stage {
  name: string;
  state: StageState;
  details: string[];
}

interface HistoryEvent {
  icon: ElementType;
  title: string;
  detail?: string;
  /** Actor, when the event has one (build / deploy events don't). */
  author?: string;
  /** Minutes since the event — rendered via the Ago formatter; also drives the
      newest-first ordering of the stream. */
  minutesAgo: number;
  tone?: "success" | "danger" | "progress" | "neutral";
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const pad = (n: number): string => String(n).padStart(2, "0");

/**
 * Reproduces ActiveCollab's standard `Ago` component (`Angie.functions.ago()`)
 * for the mock: "Just now", "N minutes ago", "Today HH:mm", "Yesterday HH:mm",
 * "MMM D. HH:mm", else "MMM D. YYYY HH:mm". Production must use the real one,
 * which also carries the absolute timestamp in a tooltip and auto-refreshes.
 */
const formatAgo = (minutesAgo: number): string => {
  const now = new Date();
  const then = new Date(now.getTime() - minutesAgo * 60000);
  if (minutesAgo < 2) {
    return "Just now";
  }
  if (minutesAgo < 60) {
    return `${Math.floor(minutesAgo)} minutes ago`;
  }
  const time = `${pad(then.getHours())}:${pad(then.getMinutes())}`;
  if (then.toDateString() === now.toDateString()) {
    return `Today ${time}`;
  }
  const yesterday = new Date(now.getTime() - 24 * 60 * 60000);
  if (then.toDateString() === yesterday.toDateString()) {
    return `Yesterday ${time}`;
  }
  const monthDay = `${MONTHS[then.getMonth()]} ${then.getDate()}.`;
  if (then.getFullYear() === now.getFullYear()) {
    return `${monthDay} ${time}`;
  }
  return `${monthDay} ${then.getFullYear()} ${time}`;
};

/** A VCS issue. Weakly referenced by any number of branches / PRs. */
interface IssueEntry {
  ref: string;
  title: string;
  repo: string;
  repoFull: string;
  state: "open" | "closed";
  updatedLabel: string;
  /** Minutes since last update — used for chronological ordering. */
  updatedMinsAgo: number;
}

type CodeStatus = "open" | "merged" | "closed" | "deleted";

/** A branch or pull request read from the VCS. */
interface CodeEntry {
  kind: "pr" | "branch";
  title: string;
  repo: string;
  repoFull: string;
  status: CodeStatus;
  /** PR head → base branches, shown under the name in the history dialog. */
  sourceBranch?: string;
  targetBranch?: string;
  /** True when this PR carries a closing keyword that will complete the task on merge. */
  closing?: boolean;
  /** Head SHA of the source branch (full); first 8 chars are shown. */
  headSha: string;
  contributors: { initials: string; bg: string }[];
  commits: { value: number; tip: string };
  diff?: { add: number; del: number; tip: string };
  comments?: { value: number; tip: string };
  noPrLabel?: string;
  updatedLabel: string;
  updatedMinsAgo: number;
  /** Issues this branch / PR references — weak, and possibly many. */
  references: string[];
  stages: Stage[];
  pipelineSupported: boolean;
  history: HistoryEvent[];
}

const mkStages = (defs: { state: StageState; details: string[] }[]): Stage[] =>
  defs.map((d, i) => ({ name: STAGE_NAMES[i], ...d }));

const issueCompleted = (i: IssueEntry): boolean => i.state === "closed";
const codeCompleted = (e: CodeEntry): boolean => e.status !== "open";
const byRecency = (
  a: { updatedMinsAgo: number },
  b: { updatedMinsAgo: number }
) => a.updatedMinsAgo - b.updatedMinsAgo;

/** External URL for an element — opens the object in the provider (new tab). */
const issueUrl = (i: IssueEntry): string =>
  `https://github.com/${i.repoFull}/issues/${i.ref.replace("#", "")}`;

const codeUrl = (e: CodeEntry): string => {
  if (e.kind === "pr") {
    const number = e.title.match(/#(\d+)/)?.[1] ?? "";
    return `https://github.com/${e.repoFull}/pull/${number}`;
  }
  return `https://github.com/${e.repoFull}/tree/${e.title}`;
};

/* ---- Issues -------------------------------------------------------- */

const ISSUES: IssueEntry[] = [
  {
    ref: "#482",
    title: "#482 Ranks reset when a contract is archived",
    repo: "backend",
    repoFull: "activecollab/backend",
    state: "open",
    updatedLabel: "20m ago",
    updatedMinsAgo: 20,
  },
  {
    ref: "#455",
    title: "#455 Retainer totals off by a rounding cent",
    repo: "backend",
    repoFull: "activecollab/backend",
    state: "open",
    updatedLabel: "3h ago",
    updatedMinsAgo: 180,
  },
  {
    ref: "#470",
    title: "#470 Cannot set a rank above 100",
    repo: "backend",
    repoFull: "activecollab/backend",
    state: "closed",
    updatedLabel: "4d ago",
    updatedMinsAgo: 5760,
  },
];

/* ---- Branches / pull requests -------------------------------------- */

const CODE_ENTRIES: CodeEntry[] = [
  {
    // Open PR with a pipeline mid-run: the CI / Build stage is "progress"
    // (blue node, white dot) while Deploy is still "todo". This is the one
    // state note 02 describes but the other examples never show — a healthy
    // task that is currently *doing something*, visible at a glance on the
    // spine. The connector into Build turns green (movement into the stage);
    // the connector into Deploy stays muted.
    kind: "pr",
    title: "#119921 Recalculate ranks when a contract is archived",
    headSha: "3d5a8f1c9b7e2604a1f8c3d5e6079b2a4c6d8e0f",
    repo: "backend",
    repoFull: "activecollab/backend",
    status: "open",
    sourceBranch: "fix/rank-archive-482",
    targetBranch: "main",
    closing: true,
    contributors: [{ initials: "DA", bg: AVATAR_COLORS.green }],
    commits: { value: 8, tip: "8 commits · last 1h ago by Dragana A." },
    diff: {
      add: 96,
      del: 12,
      tip: "96 additions, 12 deletions across 5 files",
    },
    comments: { value: 2, tip: "2 comments on the pull request" },
    updatedLabel: "10m ago",
    updatedMinsAgo: 10,
    references: ["#482"],
    stages: mkStages([
      {
        state: "done",
        details: ["fix/rank-archive-482", "on activecollab/backend"],
      },
      { state: "done", details: ["8 commits", "last 1h ago by Dragana A."] },
      { state: "done", details: ["#119921 opened", "awaiting review"] },
      {
        state: "progress",
        details: ["Build running", "CI pipeline #501 in progress"],
      },
      { state: "todo", details: ["Not deployed"] },
    ]),
    pipelineSupported: true,
    history: [
      {
        icon: GitBranchIcon,
        title: "Branch created",
        detail: "fix/rank-archive-482 by Dragana A.",
        minutesAgo: 600,
      },
      {
        icon: GitCommitIcon,
        title: "8 commits pushed",
        detail: "Last by Dragana A.: “Recalculate ranks on contract archive”",
        minutesAgo: 90,
      },
      {
        icon: GitPullRequestIcon,
        title: "Pull request opened",
        detail: "Target: main by Dragana A.",
        minutesAgo: 40,
        tone: "progress",
      },
      {
        icon: EyeIcon,
        title: "Review requested",
        detail: "Requested by Ilija S. and Marko K.",
        minutesAgo: 30,
      },
      {
        icon: GearIcon,
        title: "Build started",
        detail: "CI pipeline #501",
        minutesAgo: 10,
        tone: "progress",
      },
    ],
  },
  {
    kind: "branch",
    title: "spike/rank-cache",
    headSha: "9f3c1a2b7d4e5f60819a2b3c4d5e6f7081920304",
    repo: "backend",
    repoFull: "activecollab/backend",
    status: "open",
    contributors: [{ initials: "IS", bg: AVATAR_COLORS.purple }],
    commits: { value: 1, tip: "1 commit · 30m ago by Ilija S." },
    diff: { add: 38, del: 4, tip: "38 additions, 4 deletions across 2 files" },
    noPrLabel: "no pull request yet",
    updatedLabel: "30m ago",
    updatedMinsAgo: 30,
    references: [],
    stages: mkStages([
      {
        state: "done",
        details: ["spike/rank-cache", "on activecollab/backend"],
      },
      { state: "done", details: ["1 commit", "30m ago by Ilija S."] },
      { state: "todo", details: ["No pull request opened yet"] },
    ]),
    pipelineSupported: false,
    history: [
      {
        icon: GitBranchIcon,
        title: "Branch created",
        detail: "spike/rank-cache by Ilija Studen",
        minutesAgo: 120,
      },
      {
        icon: GitCommitIcon,
        title: "One commit pushed",
        detail: "Ilija Studen: “Prototype rank cache”",
        minutesAgo: 30,
      },
    ],
  },
  {
    kind: "branch",
    title: "feature/retainers-ui-34",
    headSha: "4a7b9c2d1e3f5061728394a5b6c7d8e9f0a1b2c3",
    repo: "frontend",
    repoFull: "activecollab/frontend",
    status: "open",
    contributors: [{ initials: "DA", bg: AVATAR_COLORS.green }],
    commits: { value: 6, tip: "6 commits · last 1h ago by Dragana A." },
    diff: {
      add: 214,
      del: 52,
      tip: "214 additions, 52 deletions across 9 files",
    },
    noPrLabel: "no pull request yet",
    updatedLabel: "1h ago",
    updatedMinsAgo: 60,
    references: ["#482"],
    stages: mkStages([
      {
        state: "done",
        details: ["feature/retainers-ui-34", "on activecollab/frontend"],
      },
      { state: "done", details: ["6 commits", "last 1h ago by Dragana A."] },
      { state: "todo", details: ["No pull request opened yet"] },
      { state: "todo", details: ["No build yet"] },
      { state: "todo", details: ["Not deployed"] },
    ]),
    pipelineSupported: true,
    history: [
      {
        icon: GitBranchIcon,
        title: "Branch created",
        detail: "feature/retainers-ui-34 by Dragana A.",
        minutesAgo: 480,
      },
      {
        icon: GitCommitIcon,
        title: "6 commits pushed",
        detail: "Last by Dragana A.: “Retainer settings screen”",
        minutesAgo: 60,
      },
    ],
  },
  {
    kind: "pr",
    title: "#119915 Contract UI rank controls",
    headSha: "b2c3d4e5f60718293a4b5c6d7e8f90a1b2c3d4e5",
    repo: "frontend",
    repoFull: "activecollab/frontend",
    status: "merged",
    sourceBranch: "feature/contracts-ui-34",
    targetBranch: "main",
    closing: true,
    contributors: [{ initials: "MK", bg: AVATAR_COLORS.blue }],
    commits: { value: 11, tip: "11 commits · last 5h ago by Marko K." },
    diff: {
      add: 168,
      del: 24,
      tip: "168 additions, 24 deletions across 6 files",
    },
    comments: { value: 5, tip: "5 comments on the pull request" },
    updatedLabel: "5h ago",
    updatedMinsAgo: 300,
    references: ["#455"],
    stages: mkStages([
      {
        state: "done",
        details: ["feature/contracts-ui-34", "on activecollab/frontend"],
      },
      { state: "done", details: ["11 commits", "last 5h ago by Marko K."] },
      { state: "done", details: ["#119915 merged", "approved by 1 reviewer"] },
      { state: "failed", details: ["Build failing", "3 of 12 checks failed"] },
      { state: "todo", details: ["Not deployed"] },
    ]),
    pipelineSupported: true,
    history: [
      {
        icon: GitBranchIcon,
        title: "Branch created",
        detail: "feature/contracts-ui-34 by Marko K.",
        minutesAgo: 11520,
      },
      {
        icon: GitCommitIcon,
        title: "6 commits pushed",
        detail: "Last by Marko K.: “Wire rank controls to contracts API”",
        minutesAgo: 10080,
      },
      {
        icon: GitPullRequestDraftIcon,
        title: "Draft pull request opened",
        detail: "Target: main by Marko K.",
        minutesAgo: 8640,
      },
      {
        icon: GearIcon,
        title: "Build started",
        detail: "CI pipeline #479",
        minutesAgo: 8639,
        tone: "progress",
      },
      {
        icon: GearFailedIcon,
        title: "Build failed",
        detail: "3 of 12 checks failed",
        minutesAgo: 8635,
        tone: "danger",
      },
      {
        icon: GitCommitIcon,
        title: "2 commits pushed",
        detail: "Last by Marko K.: “Fix failing snapshot tests”",
        minutesAgo: 7200,
      },
      {
        icon: GearIcon,
        title: "Build passed",
        detail: "All 12 checks passed · CI pipeline #482",
        minutesAgo: 7195,
        tone: "success",
      },
      {
        icon: GitPullRequestIcon,
        title: "Marked ready for review",
        author: "Marko K.",
        minutesAgo: 5760,
        tone: "progress",
      },
      {
        icon: EyeIcon,
        title: "Review requested",
        detail: "Requested by Ana P., Ilija S. and 2 more",
        minutesAgo: 5755,
      },
      {
        icon: MessageIcon,
        title: "Changes requested",
        detail: "Request on 4 files by Ana P.",
        minutesAgo: 4320,
      },
      {
        icon: GitCommitIcon,
        title: "One commit pushed",
        detail: "Marko K.: “Address review feedback”",
        minutesAgo: 4000,
      },
      {
        icon: GitPullRequestClosedIcon,
        title: "Pull request closed",
        detail: "Closed without merging by Marko K.",
        minutesAgo: 2880,
      },
      {
        icon: GitPullRequestIcon,
        title: "Pull request reopened",
        detail: "Target: main by Marko K.",
        minutesAgo: 1680,
        tone: "progress",
      },
      {
        icon: GitCommitIcon,
        title: "4 commits pushed",
        detail:
          "Last by Marko K.: “Rebuild rank controls with a new segmented layout, keyboard navigation, and analytics events”",
        minutesAgo: 1560,
      },
      {
        icon: CloudDeployIcon,
        title: "Deployed to staging",
        detail: "Release ci-4821",
        minutesAgo: 300,
        tone: "success",
      },
      {
        icon: CheckIcon,
        title: "Approved",
        detail: "2 approvals · Ana P., Ilija S.",
        minutesAgo: 180,
        tone: "success",
      },
      {
        icon: GitMergeIcon,
        title: "Pull request merged",
        detail: "Target: main by Marko K.",
        minutesAgo: 38,
        tone: "success",
      },
      {
        icon: CloudDeployFailedIcon,
        title: "Deploy failed",
        detail: "Target: production by Marko K.",
        minutesAgo: 24,
        tone: "danger",
      },
      {
        icon: CloudDeployIcon,
        title: "Deployed to production",
        detail: "Release v2026.7.3",
        minutesAgo: 1,
        tone: "success",
      },
    ],
  },
  {
    kind: "branch",
    title: "feature/retainers-34",
    headSha: "1a2b3c4d5e6f708192a3b4c5d6e7f8091a2b3c4d",
    repo: "backend",
    repoFull: "activecollab/backend",
    status: "merged",
    contributors: [{ initials: "IS", bg: AVATAR_COLORS.purple }],
    commits: { value: 14, tip: "14 commits · merged 2d ago by Ilija S." },
    diff: {
      add: 405,
      del: 37,
      tip: "405 additions, 37 deletions across 11 files",
    },
    updatedLabel: "2d ago",
    updatedMinsAgo: 2880,
    references: ["#482", "#470"],
    stages: mkStages([
      {
        state: "done",
        details: ["feature/retainers-34", "on activecollab/backend"],
      },
      { state: "done", details: ["14 commits", "merged 2d ago by Ilija S."] },
      { state: "done", details: ["Merged via #119909", "into main"] },
    ]),
    pipelineSupported: false,
    history: [
      {
        icon: GitBranchIcon,
        title: "Branch created",
        detail: "feature/retainers-34 by Ilija Studen",
        minutesAgo: 4320,
      },
      {
        icon: GitCommitIcon,
        title: "14 commits pushed",
        detail: "Last by Ilija Studen: “Rank model + datastore updates”",
        minutesAgo: 3000,
      },
      {
        icon: GitMergeIcon,
        title: "Branch merged",
        detail: "Target: main by Ilija Studen",
        minutesAgo: 2880,
        tone: "success",
      },
    ],
  },
  {
    kind: "pr",
    title: "#119909 Allow and update ranks across datastores",
    headSha: "c4d5e6f70819202a3b4c5d6e7f8091a2b3c4d5e6",
    repo: "backend",
    repoFull: "activecollab/backend",
    status: "merged",
    sourceBranch: "feature/retainers-34",
    targetBranch: "main",
    contributors: [
      { initials: "IS", bg: AVATAR_COLORS.purple },
      { initials: "MK", bg: AVATAR_COLORS.blue },
    ],
    commits: { value: 14, tip: "14 commits · merged 2d ago by Ilija S." },
    diff: {
      add: 405,
      del: 37,
      tip: "405 additions, 37 deletions across 11 files",
    },
    comments: { value: 12, tip: "12 comments on the pull request" },
    updatedLabel: "2d ago",
    updatedMinsAgo: 2900,
    references: ["#482", "#470"],
    stages: mkStages([
      {
        state: "done",
        details: ["feature/retainers-34", "on activecollab/backend"],
      },
      { state: "done", details: ["14 commits", "last 2d ago by Ilija S."] },
      { state: "done", details: ["#119909 merged", "approved by 2 reviewers"] },
    ]),
    pipelineSupported: false,
    history: [
      {
        icon: GitPullRequestIcon,
        title: "Pull request opened",
        detail: "Target: main by Ilija Studen",
        minutesAgo: 3000,
        tone: "progress",
      },
      {
        icon: CheckIcon,
        title: "Approved",
        detail: "2 approvals · Marko K., Janko S.",
        minutesAgo: 2905,
        tone: "success",
      },
      {
        icon: GitMergeIcon,
        title: "Pull request merged",
        detail: "Target: main by Ilija Studen",
        minutesAgo: 2880,
        tone: "success",
      },
    ],
  },
  {
    kind: "branch",
    title: "hotfix/rank-typo",
    headSha: "7e8f90a1b2c3d4e5f60718293a4b5c6d7e8f9012",
    repo: "backend",
    repoFull: "activecollab/backend",
    status: "deleted",
    contributors: [{ initials: "MK", bg: AVATAR_COLORS.blue }],
    commits: { value: 2, tip: "2 commits · deleted 5d ago" },
    diff: { add: 6, del: 3, tip: "6 additions, 3 deletions across 1 file" },
    updatedLabel: "5d ago",
    updatedMinsAgo: 7000,
    references: ["#470"],
    stages: mkStages([
      {
        state: "done",
        details: ["hotfix/rank-typo", "on activecollab/backend"],
      },
      { state: "done", details: ["2 commits", "by Marko K."] },
      { state: "todo", details: ["No pull request opened"] },
    ]),
    pipelineSupported: false,
    history: [
      {
        icon: GitBranchIcon,
        title: "Branch created",
        detail: "hotfix/rank-typo by Marko K.",
        minutesAgo: 8640,
      },
      {
        icon: GitCommitIcon,
        title: "2 commits pushed",
        detail: "Last by Marko K.: “Fix rank label typo”",
        minutesAgo: 8600,
      },
      {
        icon: TrashIcon,
        title: "Branch deleted",
        detail: "Soft deleted by Marko K.",
        minutesAgo: 7000,
      },
    ],
  },
  {
    kind: "pr",
    title: "#119870 Clamp rank to valid range",
    headSha: "d5e6f708192a3b4c5d6e7f8091a2b3c4d5e6f701",
    repo: "backend",
    repoFull: "activecollab/backend",
    status: "merged",
    sourceBranch: "fix/rank-clamp-33",
    targetBranch: "main",
    contributors: [{ initials: "MK", bg: AVATAR_COLORS.blue }],
    commits: { value: 4, tip: "4 commits · merged 5d ago by Marko K." },
    diff: { add: 52, del: 8, tip: "52 additions, 8 deletions across 3 files" },
    updatedLabel: "5d ago",
    updatedMinsAgo: 7200,
    references: ["#470"],
    stages: mkStages([
      {
        state: "done",
        details: ["fix/rank-clamp-33", "on activecollab/backend"],
      },
      { state: "done", details: ["4 commits", "merged 5d ago by Marko K."] },
      { state: "done", details: ["#119870 merged", "approved by 1 reviewer"] },
    ]),
    pipelineSupported: false,
    history: [
      {
        icon: GitBranchIcon,
        title: "Branch created",
        detail: "fix/rank-clamp-33 by Marko K.",
        minutesAgo: 8640,
      },
      {
        icon: GitPullRequestIcon,
        title: "Pull request opened",
        detail: "Target: main by Marko K.",
        minutesAgo: 8600,
        tone: "progress",
      },
      {
        icon: GitMergeIcon,
        title: "Pull request merged",
        detail: "Target: main by Marko K.",
        minutesAgo: 7200,
        tone: "success",
      },
    ],
  },
  {
    kind: "pr",
    title: "#119888 Cache ranks per datastore",
    headSha: "a1b2c3d4e5f60718293a4b5c6d7e8f90a1b2c3d4",
    repo: "backend",
    repoFull: "activecollab/backend",
    status: "closed",
    sourceBranch: "feature/rank-datastore-cache",
    targetBranch: "main",
    contributors: [{ initials: "IS", bg: AVATAR_COLORS.purple }],
    commits: { value: 5, tip: "5 commits · closed 3d ago by Ilija S." },
    diff: {
      add: 120,
      del: 15,
      tip: "120 additions, 15 deletions across 4 files",
    },
    comments: { value: 3, tip: "3 comments on the pull request" },
    updatedLabel: "3d ago",
    updatedMinsAgo: 4320,
    references: ["#455"],
    stages: mkStages([
      {
        state: "done",
        details: ["feature/rank-datastore-cache", "on activecollab/backend"],
      },
      { state: "done", details: ["5 commits", "last 3d ago by Ilija S."] },
      { state: "todo", details: ["#119888 closed without merging"] },
    ]),
    pipelineSupported: false,
    history: [
      {
        icon: GitPullRequestIcon,
        title: "Pull request opened",
        detail: "Target: main by Ilija Studen",
        minutesAgo: 5760,
        tone: "progress",
      },
      {
        icon: GitPullRequestClosedIcon,
        title: "Pull request closed",
        detail: "Closed without merging by Ilija Studen",
        minutesAgo: 4320,
      },
    ],
  },
];

/* ================================================================== */
/* Spine + history dialog (for branches / pull requests)               */
/* ================================================================== */

const StageTip = ({ name, details }: Stage): ReactElement => (
  <span style={{ display: "block", textAlign: "left", lineHeight: 1.5 }}>
    <span style={{ display: "block", fontWeight: 700 }}>Stage: {name}</span>
    {details.map((d, i) => (
      <span key={`${name}-${i}`} style={{ display: "block" }}>
        {d}
      </span>
    ))}
  </span>
);

const StyledSpineSequence = styled(StepSequence)`
  align-self: center;
  flex-shrink: 0;
`;

/** Stage state → the generic look `StepSequence` renders it with. */
const STAGE_STEP: Record<
  StageState,
  Pick<IStepSequenceStep, "tone" | "fill" | "icon">
> = {
  done: {
    tone: "success",
    fill: "solid",
    icon: <CheckIcon fill="var(--only-white)" width={14} height={14} />,
  },
  progress: { tone: "info", fill: "solid" },
  failed: {
    tone: "danger",
    fill: "solid",
    icon: <CloseIcon fill="var(--only-white)" width={12} height={12} />,
  },
  todo: { tone: "neutral", fill: "outline" },
};

const Spine = ({
  items,
  onClick,
}: {
  items: Stage[];
  onClick: () => void;
}): ReactElement => (
  <StyledSpineSequence
    ariaLabel="Open source code history"
    onClick={onClick}
    steps={items.map((s) => ({
      ...STAGE_STEP[s.state],
      key: s.name,
      tooltip: <StageTip {...s} />,
    }))}
  />
);

/* The whole content scrolls together (header + stream + note) once the stream
   is long — the dialog title bar stays fixed, everything below scrolls. */
const StyledHistory = styled.div`
  font-family: ${SANS};
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 4px;

  /* PR/branch details, then a separator with equal standard spacing above and
     below it, then the event stream. */
  .hist-header {
    padding-bottom: 20px;
    margin-bottom: 20px;
    border-bottom: 1px solid var(--border-primary);
  }

  .hist-name {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .hist-branches {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 8px;
    font-size: 13px;
    color: var(--color-theme-600);
  }
  .hist-branch {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .hist-repo {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    font-size: 13px;
    color: var(--color-theme-600);
  }

  /* Status label beside the name — identical to the one in the linked-elements
     list (CodeStatusChip), so the two surfaces read the same. */
  .sc-chip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    height: 20px;
    padding: 0 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    flex-shrink: 0;
  }
  .sc-chip.done {
    color: var(--color-primary);
    background: color-mix(in srgb, var(--color-primary) 15%, transparent);
  }
  .sc-chip.gone {
    color: var(--color-theme-900);
    background: var(--color-theme-200);
    border: 1px solid var(--border-primary);
  }
  .sc-chip.fail {
    color: var(--red-alert);
    background: color-mix(in srgb, var(--red-alert) 15%, transparent);
  }

  /* No connector line — glyph is vertically centred within the row height. */
  .hist-event {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 20px;
  }
  .hist-event:last-child {
    padding-bottom: 0;
  }

  /* Glyph only — no circle. Sized to hold a 22px icon. */
  .hist-icon {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .hist-body {
    flex: 1 1 auto;
    min-width: 0;
  }

  /* Title row: the name truncates with an ellipsis; the success / failure
     marker stays pinned beside it. */
  .hist-title {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }
  .hist-title > *:first-child {
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .hist-status {
    flex-shrink: 0;
    display: inline-flex;
  }

  /* Secondary event text (detail, author) and the timestamp match the muted
     branch / repository lines under the name; each stays on one row and
     truncates with an ellipsis. */
  .hist-line {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 13px;
    line-height: 1.5;
    color: var(--color-theme-600);
  }

  /* Timestamp sits right-aligned beside the event, aligned to the first line
     (the glyph stays vertically centred in the row). */
  .hist-meta {
    flex-shrink: 0;
    align-self: flex-start;
    text-align: right;
    white-space: nowrap;
  }

  /* No-pipeline heads-up — a light single line with an info glyph, not a box. */
  .hist-note {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 16px;
    color: var(--color-theme-500);
  }
  .hist-note svg {
    flex-shrink: 0;
  }
`;

const HistoryDialog = ({
  entry,
  onClose,
}: {
  entry: CodeEntry | null;
  onClose: () => void;
}): ReactElement => (
  <Dialog open={!!entry} onClose={onClose} enableBackgroundClick>
    <Dialog.Title disableDefaultHeading>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Header3>
          {entry?.kind === "branch" ? "Branch History" : "Pull Request History"}
        </Header3>
        <IconButton type="button" variant="text gray" onClick={onClose}>
          <CancelCrossIcon />
        </IconButton>
      </div>
    </Dialog.Title>
    <Dialog.ContentDivider />
    <Dialog.Content>
      {entry && (
        <StyledHistory>
          <div className="hist-header">
            <div className="hist-name">
              <Body1 weight="medium">
                {entry.status === "deleted" ? (
                  entry.title
                ) : (
                  <TitleLink
                    href={codeUrl(entry)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {entry.title}
                  </TitleLink>
                )}
              </Body1>
              <CodeStatusChip entry={entry} />
            </div>

            {entry.kind === "pr" &&
              entry.sourceBranch &&
              entry.targetBranch && (
                <div className="hist-branches">
                  <span className="hist-branch">
                    <GitBranchIcon width={14} height={14} />
                    {entry.sourceBranch}
                  </span>
                  <ArrowRightIcon width={14} height={14} />
                  <span className="hist-branch">
                    <GitBranchIcon width={14} height={14} />
                    {entry.targetBranch}
                  </span>
                </div>
              )}

            <span className="hist-repo">
              <GitRepositoryIcon width={14} height={14} />
              {entry.repoFull}
            </span>
          </div>

          {[...entry.history]
            .sort((a, b) => a.minutesAgo - b.minutesAgo)
            .map((ev, i) => (
              <div className="hist-event" key={`${ev.title}-${i}`}>
                <span className="hist-icon">
                  <ev.icon
                    width={22}
                    height={22}
                    {...iconColorProps(ev.icon)}
                  />
                </span>
                <div className="hist-body">
                  <div className="hist-title">
                    <Body1 weight="medium">{ev.title}</Body1>
                    {ev.tone === "success" && (
                      <span className="hist-status">
                        <CheckIcon
                          width={16}
                          height={16}
                          fill="var(--color-sucess-green)"
                        />
                      </span>
                    )}
                    {ev.tone === "danger" && (
                      <span className="sc-chip fail">
                        <CloseIcon
                          width={12}
                          height={12}
                          fill="var(--red-alert)"
                        />
                        Failed
                      </span>
                    )}
                  </div>
                  {ev.detail && <span className="hist-line">{ev.detail}</span>}
                  {ev.author && <span className="hist-line">{ev.author}</span>}
                </div>
                <div className="hist-meta">
                  <span className="hist-line">{formatAgo(ev.minutesAgo)}</span>
                </div>
              </div>
            ))}

          {!entry.pipelineSupported && (
            <div className="hist-note">
              <InfoSmallIcon width={15} height={15} />
              <Caption1 color="secondary">
                No CI or deployment pipeline is configured for this repository.
              </Caption1>
            </div>
          )}
        </StyledHistory>
      )}
    </Dialog.Content>
  </Dialog>
);

/* ================================================================== */
/* Source Code section — flat list, split open / completed             */
/* ================================================================== */

const StyledSourceCode = styled.div<{ $flush?: boolean }>`
  font-family: ${SANS};
  margin: 0 0 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-primary);

  /* In the dialog the section is the whole body, so drop the outer spacing and
     the bottom rule (no separator after the last linked element). */
  ${(p) =>
    p.$flush &&
    css`
      margin: 0;
      padding-bottom: 0;
      border-bottom: none;
    `}

  .sc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 4px;
  }

  /* "Show N more completed elements" toggle — a DS "text colored" button */
  .sc-completed-row {
    display: flex;
    align-items: center;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--border-primary);
  }

  /* "Link Branch or Pull Request" action at the bottom of the section */
  .sc-add {
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px solid var(--border-primary);
  }
  .sc-add .c--add-to-list-btn svg {
    box-sizing: content-box;
  }

  .sc-item {
    padding: 12px 0;
  }
  .sc-item + .sc-item {
    border-top: 1px solid var(--border-primary);
  }

  .sc-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  .sc-main {
    flex: 1 1 0%;
    min-width: 0;
  }

  .sc-name {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: nowrap;
    min-width: 0;
  }

  .sc-lead {
    flex-shrink: 0;
    display: inline-flex;
    color: var(--color-theme-600);
  }

  .sc-title {
    flex: 0 1 auto;
    min-width: 0;
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  /* Completed items keep the regular text color for readability; a deleted
     branch is still marked with a strikethrough (no muted color). */
  .sc-title.strike {
    text-decoration: line-through;
  }
  /* Closed PR — struck through by default, but the strike lifts on hover so it
     reads as a normal PR link (the inner link turns primary on hover). */
  .sc-title.strike-link {
    text-decoration: line-through;
  }
  .sc-title.strike-link:hover {
    text-decoration: none;
  }

  .sc-sigs {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 10px;
    font-size: 12px;
    color: var(--color-theme-600);
  }

  .sc-sig {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  /* "Closing" marker on an active PR that will complete the task on merge. */
  .sc-closing {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    cursor: default;
  }

  /* Head SHA renders in a monospace face; size / colour come from the Caption 1
     it is rendered with (tertiary → theme-600), matching the app. */
  .sc-sha {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  }

  .sc-muted {
    color: var(--color-theme-600);
  }

  /* issue references on a branch / PR — weak, and possibly several */
  .sc-refs {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .sc-refs .ref-link {
    color: var(--color-primary);
    font-weight: 500;
  }
`;

/* Element name — a link that opens the object in a new tab. Keeps the row's
   typography (inherits color + weight), turns primary on hover, link cursor. */
const TitleLink = styled.a`
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: color 200ms ease;

  &:hover {
    color: var(--color-primary);
  }
`;

/* Status label chip — the DS `Chip` styled to match the app's
   `SourceCodeStatusChip`: a "done" tone (primary text on a translucent-primary
   fill) and a "gone" tone (muted text on a bordered theme-200 fill). */
const CHIP_BASE_STYLE: React.CSSProperties = {
  height: "1.25rem",
  paddingLeft: "0.5rem",
  paddingRight: "0.5rem",
  borderRadius: "0.375rem",
  gap: "3px",
  flexShrink: 0,
};

const CHIP_DONE_STYLE: React.CSSProperties = {
  ...CHIP_BASE_STYLE,
  backgroundColor: "color-mix(in srgb, var(--color-primary) 15%, transparent)",
};

const CHIP_GONE_STYLE: React.CSSProperties = {
  ...CHIP_BASE_STYLE,
  backgroundColor: "var(--color-theme-200)",
  border: "1px solid var(--border-primary)",
};

/* Info tone — the default (blue) InfoBox recipe: blueSky tint + solid border. */
const CHIP_INFO_STYLE: React.CSSProperties = {
  ...CHIP_BASE_STYLE,
  backgroundColor: `${colors.blueSky.color}1A`,
  border: `1px solid ${colors.blueSky.color}`,
};

const StatusChip = ({
  label,
  tone,
  glyph,
}: {
  label: string;
  tone: "done" | "info" | "gone";
  glyph?: ReactNode;
}): ReactElement => (
  <Chip
    label={label}
    startAdornment={glyph}
    color={
      tone === "done"
        ? "var(--color-primary)"
        : tone === "info"
        ? colors.blueSky.lighter
        : undefined
    }
    style={
      tone === "done"
        ? CHIP_DONE_STYLE
        : tone === "info"
        ? CHIP_INFO_STYLE
        : CHIP_GONE_STYLE
    }
    typographyProps={{ variant: "Caption 1", weight: "bold", align: "center" }}
  />
);

/* Contributor avatars — the app renders real user images through the DS
   `AvatarGroup` (`ContributorAvatars.jsx`). The mock feeds that same component
   initials avatars via `MockAvatar`, which honours AvatarGroup's cloneElement
   contract (it injects `size`, `alt` and `style` — the overlap margin + paper
   ring), so the grouping, overlap and "+N" overflow behave identically. */
const CONTRIBUTOR_NAMES: Record<string, string> = {
  DA: "Dragana A.",
  IS: "Ilija Studen",
  MK: "Marko K.",
};

const MockAvatar = React.forwardRef<
  HTMLDivElement,
  {
    size?: number;
    bg: string;
    initials: string;
    /** Injected by AvatarGroup for the tooltip label; not rendered here. */
    alt?: string;
  } & React.HTMLAttributes<HTMLDivElement>
  // `alt` is pulled out only so it is not spread onto the <div> (invalid DOM
  // attribute); AvatarGroup has already read it for the tooltip label.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
>(({ size = 20, bg, initials, alt, ...rest }, ref) => (
  // `...rest` carries the props AvatarGroup and its Tooltip inject via
  // cloneElement — `style` (overlap margin + paper ring) and the hover handlers
  // (`onMouseOver` / `onMouseLeave`) — plus the forwarded `ref`, all of which
  // must reach the DOM node for the tooltip to open.
  <RoundAvatar ref={ref} $bg={bg} $size={size} $bordered={false} {...rest}>
    {initials}
  </RoundAvatar>
));
MockAvatar.displayName = "MockAvatar";

/* The app removes AvatarGroup's default padding with `tw-p-0`; the mock does
   the same through a styled wrapper (`tw-` classes don't run in Storybook). */
const StyledAvatarGroup = styled(AvatarGroup)`
  && {
    padding: 0;
  }
`;

const ContributorAvatars = ({
  contributors,
}: {
  contributors: { initials: string; bg: string }[];
}): ReactElement => (
  <StyledAvatarGroup limit={4} size={20} hasTooltip>
    {contributors.map((c) => (
      <MockAvatar
        key={c.initials}
        bg={c.bg}
        initials={c.initials}
        alt={CONTRIBUTOR_NAMES[c.initials] ?? c.initials}
      />
    ))}
  </StyledAvatarGroup>
);

const IssueRow = ({ issue }: { issue: IssueEntry }): ReactElement => {
  const closed = issue.state === "closed";

  return (
    <div className="sc-row">
      <div className="sc-main">
        <div className="sc-name">
          <span
            className="sc-lead"
            style={{ color: closed ? "var(--color-primary)" : undefined }}
          >
            <IssueStateIcon closed={closed} />
          </span>
          <Body1 weight="medium" className="sc-title">
            <TitleLink
              href={issueUrl(issue)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {issue.title}
            </TitleLink>
          </Body1>
          {closed && (
            <StatusChip
              label="Closed"
              tone="done"
              glyph={<IssueStateIcon closed size={12} />}
            />
          )}
        </div>

        <div className="sc-sigs">
          <Tooltip title={`Repository: ${issue.repoFull}`}>
            <span className="sc-sig">
              <GitRepositoryIcon width={14} height={14} />
              {issue.repo}
            </span>
          </Tooltip>

          <span className="sc-muted">{issue.updatedLabel}</span>
        </div>
      </div>
    </div>
  );
};

const CodeStatusChip = ({
  entry,
}: {
  entry: CodeEntry;
}): ReactElement | null => {
  if (entry.status === "merged") {
    return (
      <StatusChip
        label="Merged"
        tone="done"
        glyph={
          <GitPullRequestIcon
            width={12}
            height={12}
            fill="var(--color-primary)"
          />
        }
      />
    );
  }
  if (entry.status === "deleted") {
    return (
      <StatusChip
        label="Deleted"
        tone="gone"
        glyph={
          <TrashIcon width={12} height={12} fill="var(--color-theme-900)" />
        }
      />
    );
  }
  if (entry.status === "closed") {
    return <StatusChip label="Closed" tone="gone" />;
  }
  // Open branches / PRs show no status label.
  return null;
};

const CodeRow = ({
  entry,
  onOpenHistory,
}: {
  entry: CodeEntry;
  onOpenHistory: () => void;
}): ReactElement => {
  const deleted = entry.status === "deleted";
  // A closed-without-merge PR is still a live link, but its name is struck
  // through to signal the dead-end; the strike lifts on hover so it reads as a
  // normal PR link.
  const closedPr = entry.kind === "pr" && entry.status === "closed";

  return (
    <div className="sc-row">
      <div className="sc-main">
        <div className="sc-name">
          <span className="sc-lead">
            {entry.kind === "pr" ? (
              <GitPullRequestIcon width={16} height={16} />
            ) : (
              <GitBranchIcon width={16} height={16} />
            )}
          </span>
          {deleted ? (
            <Body1 weight="medium" className="sc-title strike">
              {entry.title}
            </Body1>
          ) : (
            <Body1
              weight="medium"
              className={closedPr ? "sc-title strike-link" : "sc-title"}
            >
              <TitleLink
                href={codeUrl(entry)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {entry.title}
              </TitleLink>
            </Body1>
          )}
          <CodeStatusChip entry={entry} />
          {entry.kind === "pr" && entry.status === "open" && entry.closing && (
            <Tooltip title="Marks the task as completed when this pull request is merged.">
              <span className="sc-closing">
                <StatusChip
                  label="Closing"
                  tone="info"
                  glyph={
                    <CheckIcon
                      width={12}
                      height={12}
                      fill={colors.blueSky.lighter}
                    />
                  }
                />
              </span>
            </Tooltip>
          )}
        </div>

        <div className="sc-sigs">
          <Tooltip title={`Repository: ${entry.repoFull}`}>
            <span className="sc-sig">
              <GitRepositoryIcon width={14} height={14} />
              {entry.repo}
            </span>
          </Tooltip>

          <ContributorAvatars contributors={entry.contributors} />

          <Tooltip title={entry.commits.tip}>
            <Caption1 color="tertiary" className="sc-sig">
              <GitCommitIcon width={14} height={14} />
              {entry.commits.value}
            </Caption1>
          </Tooltip>

          <Tooltip title={`Head commit: ${entry.headSha}`}>
            <Caption1 color="tertiary" className="sc-sig sc-sha">
              {entry.headSha.slice(0, 8)}
            </Caption1>
          </Tooltip>

          {entry.diff && (
            <Tooltip title={entry.diff.tip}>
              <span className="sc-sig sc-diff">
                <Caption1 color="success">+{entry.diff.add}</Caption1>
                <Caption1 color="alert">−{entry.diff.del}</Caption1>
              </span>
            </Tooltip>
          )}

          {entry.comments && (
            <Tooltip title={entry.comments.tip}>
              <Caption1 color="tertiary" className="sc-sig">
                <MessageSmallIcon />
                {entry.comments.value}
              </Caption1>
            </Tooltip>
          )}

          <span className="sc-muted">{entry.updatedLabel}</span>
        </div>
      </div>

      <Spine items={entry.stages} onClick={onOpenHistory} />
    </div>
  );
};

const CompletedHeader = ({
  expanded,
  count,
  onToggle,
}: {
  expanded: boolean;
  count: number;
  onToggle: () => void;
}): ReactElement => (
  <div className="sc-completed-row">
    <Button
      type="button"
      variant="text colored"
      onClick={onToggle}
      aria-expanded={expanded}
      style={{ marginLeft: -8 }}
    >
      {expanded
        ? "Hide completed elements"
        : `Show ${count} more completed elements`}
    </Button>
  </div>
);

type SectionVariant = "mix" | "open" | "completed";

const SourceCodeSection = ({
  variant = "mix",
  issues,
  code,
  showHeader = true,
  showLink = true,
  flush = false,
}: {
  variant?: SectionVariant;
  /** Optional per-task data. Defaults to the built-in demo mix so the existing
      Task Source stories are unchanged. The Tasks List column passes a task's
      own linked elements so the dialog matches the row. */
  issues?: IssueEntry[];
  code?: CodeEntry[];
  /** The dialog gives its own "Source Code" title, so it hides this header. */
  showHeader?: boolean;
  /** The read-only Source Code dialog hides the "Link" action. */
  showLink?: boolean;
  /** Remove the section's outer spacing and bottom rule (used in the dialog). */
  flush?: boolean;
}): ReactElement => {
  const [openEntry, setOpenEntry] = useState<CodeEntry | null>(null);
  const [completedExpanded, setCompletedExpanded] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  const issuesData = issues ?? ISSUES;
  const codeData = code ?? CODE_ENTRIES;

  const openIssues = issuesData
    .filter((i) => !issueCompleted(i))
    .sort(byRecency);
  const openCode = codeData.filter((e) => !codeCompleted(e)).sort(byRecency);

  const completed: {
    key: string;
    updatedMinsAgo: number;
    node: ReactElement;
  }[] = [
    ...issuesData.filter(issueCompleted).map((issue) => ({
      key: issue.ref,
      updatedMinsAgo: issue.updatedMinsAgo,
      node: <IssueRow issue={issue} />,
    })),
    ...codeData.filter(codeCompleted).map((entry) => ({
      key: `${entry.repo}-${entry.title}`,
      updatedMinsAgo: entry.updatedMinsAgo,
      node: <CodeRow entry={entry} onOpenHistory={() => setOpenEntry(entry)} />,
    })),
  ].sort(byRecency);

  const showOpen = variant === "mix" || variant === "open";
  const showCompleted = variant === "mix" || variant === "completed";
  // In the "completed" variant there is no collapsible header and everything is
  // shown; in "mix" the section is collapsed by default.
  const completedVisible = variant === "completed" ? true : completedExpanded;

  return (
    <StyledSourceCode $flush={flush}>
      {showHeader && (
        <div className="sc-header">
          <Body1 weight="bold">Source Code</Body1>
          <LinkingCommandsButton />
        </div>
      )}

      {showOpen &&
        openIssues.map((issue) => (
          <div className="sc-item" key={issue.ref}>
            <IssueRow issue={issue} />
          </div>
        ))}
      {showOpen &&
        openCode.map((entry) => (
          <div className="sc-item" key={`${entry.repo}-${entry.title}`}>
            <CodeRow entry={entry} onOpenHistory={() => setOpenEntry(entry)} />
          </div>
        ))}

      {/* When expanded, completed rows join the same list above the toggle;
          the toggle ("Show N more…" / "Hide…") always sits at the bottom. */}
      {showCompleted &&
        completedVisible &&
        completed.map((c) => (
          <div className="sc-item" key={c.key}>
            {c.node}
          </div>
        ))}
      {showCompleted && completed.length > 0 && variant === "mix" && (
        <CompletedHeader
          expanded={completedExpanded}
          count={completed.length}
          onToggle={() => setCompletedExpanded((v) => !v)}
        />
      )}

      {showLink && (
        <div className="sc-add">
          <AddToListButton
            text="Link Branch or Pull Request"
            style={{ marginLeft: -4 }}
            onClick={() => setLinkOpen(true)}
          />
        </div>
      )}

      <HistoryDialog entry={openEntry} onClose={() => setOpenEntry(null)} />
      <LinkBranchOrPullRequestDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
      />
    </StyledSourceCode>
  );
};

export type {
  StageState,
  Stage,
  HistoryEvent,
  IssueEntry,
  CodeStatus,
  CodeEntry,
  SectionVariant,
};

export {
  Spine,
  HistoryDialog,
  IssueRow,
  CodeRow,
  CompletedHeader,
  SourceCodeSection,
};
